/**
 * COURIER LAB — Code101 chapter 2, where "slow" stops being one thing.
 *
 * FOUNDER, 2026-08-14: "ch2 next".
 *
 * ── THE ONE THING THIS TEACHES ────────────────────────────────────────────────────────────────
 * The chapter's own sentence: "This single distinction — is the bottleneck the machine or the
 * network? — is the kind of question a literate user starts asking instead of just feeling
 * frustrated." So the round trip is always shown in TWO clocks that never merge into one. Move the
 * far machine from the same city to a different continent and the journey bar swells while the
 * machine's bar does not move at all; the total changed and the machine did nothing. That is the
 * whole lesson, and it is one drag long.
 *
 * ── AND THE SECOND SENTENCE IT SERVES ─────────────────────────────────────────────────────────
 * §6: "A literate user, when something does not work, asks: is it me, is it the courier, or is it
 * them? Most users blame 'the app' for all three." So the three failures are switches, and the lab
 * answers his question in his three words by reporting WHERE the walk stopped — not by grading.
 *
 * ── WHAT IT REFUSES ───────────────────────────────────────────────────────────────────────────
 * It measures nothing real. Every figure on screen is the module's own — 20 ms, 250 ms, 3 s, and
 * the 200-800 ms round trip — animated, not sampled. A lab that timed the learner's actual
 * connection would be a speed test wearing a lesson's clothes, and its numbers would contradict the
 * page beside it on a bad day. The animation is a DEPICTION of his figures and says so.
 *
 * It also does not model packets individually. §4 describes them — "broken into many small pieces…
 * some packets may take slightly different routes" — and drawing a dozen dots would suggest the
 * lab knows how many there are. It does not, and neither does the module.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  PLACES, PLACES_SOURCE, STEPS, STEPS_SOURCE, FAULTS, FAULTS_SOURCE, THE_QUESTION,
  roundTrip, GMAIL_RANGE, inGmailRange, TRANSLATIONS, TRANSLATIONS_SOURCE,
} from './courier.mjs';

/* The walk is shown at a watchable pace, not at the real one — 3 s of village latency would be
   three seconds of nothing, and 20 ms of city latency would be invisible. The DEPICTION is scaled;
   the NUMBERS printed beside it are his, unscaled. Both facts are on the surface. */
const PACE_MS = 620;

export default function CourierLab() {
  const [place, setPlace] = useState('continent');
  const [workMs, setWorkMs] = useState(60);
  const [fault, setFault] = useState('none');
  const [at, setAt] = useState(-1);
  const [walking, setWalking] = useState(false);
  const timer = useRef(0);

  const trip = useMemo(() => roundTrip({ place, workMs, fault }), [place, workMs, fault]);
  const f = FAULTS[fault];
  const cur = at >= 0 ? trip.steps[at] : null;

  const stop = useCallback(() => { clearTimeout(timer.current); setWalking(false); }, []);
  const reset = useCallback(() => { stop(); setAt(-1); }, [stop]);
  useEffect(() => reset, [reset]);
  /* Any change to the conditions puts the courier back at the start — a walk half-done under the
     old distance would show step timings from a trip that no longer exists. */
  useEffect(() => { reset(); }, [place, workMs, fault, reset]);

  useEffect(() => {
    if (!walking) return undefined;
    const next = at + 1;
    if (next >= trip.steps.length || (at >= 0 && !trip.steps[at].reached)) { setWalking(false); return undefined; }
    timer.current = setTimeout(() => setAt(next), PACE_MS);
    return () => clearTimeout(timer.current);
  }, [walking, at, trip.steps]);

  const pct = trip.totalMs ? Math.max(2, Math.round((100 * trip.journeyMs) / trip.totalMs)) : 0;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Courier lab · one round trip</div>
      <h2 className="g-h2 lab-h">Is it the machine, or is it the network?</h2>
      <p className="g-body">
        Type <b>gmail.com</b> and press enter and five things happen, all of them invisible. Walk
        them. Then move the far machine further away and watch which of the two clocks moves —
        because when an app feels slow, only one of them usually did.
      </p>

      {/* ── CONDITIONS ── */}
      <div className="lab-task-head">
        <span className="g-label">The far machine is</span>
        {PLACES.map((p) => (
          <button key={p.id} className={`g-btn ${place === p.id ? 'g-btn-primary' : ''}`}
            onClick={() => setPlace(p.id)}>{p.label.replace('a machine in ', '').replace('a machine on ', '')}</button>
        ))}
      </div>
      <div className="lab-slider">
        <span className="g-label">The machine's own work — {workMs} ms</span>
        <input type="range" min={10} max={600} step={10} value={workMs} className="lab-range"
          onChange={(e) => setWorkMs(Number(e.target.value))} />
      </div>
      <div className="lab-task-head">
        <span className="g-label">Something wrong?</span>
        {Object.values(FAULTS).map((x) => (
          <button key={x.id} className={`g-btn ${fault === x.id ? 'g-btn-primary' : ''}`}
            onClick={() => setFault(x.id)}>{x.label}</button>
        ))}
      </div>

      {/* ── THE FIVE STEPS ── */}
      <div className="lab-task-head" style={{ marginTop: 'var(--g-3)' }}>
        <button className="g-btn g-btn-primary" onClick={() => { if (at >= trip.steps.length - 1) setAt(-1); setWalking((w) => !w); }}>
          {walking ? 'Pause' : 'Send the request'}
        </button>
        <button className="g-btn" onClick={() => { stop(); setAt((i) => Math.min(i + 1, trip.steps.length - 1)); }}>Step</button>
        <button className="g-btn" onClick={reset}>Reset</button>
      </div>

      <div className="cou-steps">
        {trip.steps.map((s, i) => {
          const state = at < 0 ? 'idle' : (i < at || (i === at && s.reached)) ? (s.reached ? 'done' : 'stop')
            : i === at ? (s.reached ? 'now' : 'stop') : 'idle';
          return (
            <div key={s.n} className={`cou-step cou-${state} cou-who-${s.who}`}>
              <div className="cou-head">
                <span className="cou-n">{s.n}</span>
                <b>{s.name}</b>
                <span className="cou-sp" />
                {s.reached && s.ms > 0 && <span className="cou-ms">{s.ms} ms</span>}
                {!s.reached && at >= i && <span className="cou-x">stopped here</span>}
              </div>
              {(state === 'now' || state === 'stop') && <p className="g-body cou-text">{s.text}</p>}
            </div>
          );
        })}
      </div>

      {/* ── THE TWO CLOCKS. The whole chapter, in one bar. ── */}
      <div className="lab-task">
        <div className="g-label">Where the wait went</div>
        {trip.arrived ? (
          <>
            <div className="cou-bar" role="img"
              aria-label={`journey ${trip.journeyMs} ms, machine ${trip.machineMs} ms`}>
              <div className="cou-net" style={{ width: `${pct}%` }}>
                {pct > 18 && <span>the courier · {trip.journeyMs} ms</span>}
              </div>
              <div className="cou-mac" style={{ width: `${100 - pct}%` }}>
                {100 - pct > 18 && <span>the machine · {trip.machineMs} ms</span>}
              </div>
            </div>
            <div className="lab-readout">
              <div><span className="g-label">The whole round trip</span>
                <Value of={computed(`${trip.totalMs} ms`)} /></div>
              <div><span className="g-label">Of which the journey</span>
                <Value of={computed(`${trip.journeyShare}%`)} /></div>
              <div><span className="g-label">The bottleneck is</span>
                <span className="lab-num">{trip.bottleneck}</span></div>
            </div>
            {inGmailRange(trip.totalMs) && (
              <p className="g-body g-dim cou-note">
                <Value of={cited(`That entire round-trip happened in ${GMAIL_RANGE.lowMs} to ${GMAIL_RANGE.highMs} milliseconds.`, GMAIL_RANGE.source)} />
                {' '}This trip is inside that range.
              </p>
            )}
          </>
        ) : (
          <div className="cou-fail">
            <p className="g-body"><b>{f.label}.</b> {f.text}</p>
            <p className="g-body cou-blame">
              Nothing arrived. The walk stopped at step {trip.failedAt} — <b>{trip.steps[trip.failedAt - 1].name}</b>.
              So the answer to the question is: <b>{trip.blame}</b>.
            </p>
            <p className="g-body g-dim"><Value of={cited(f.verdict, FAULTS_SOURCE)} /></p>
          </div>
        )}
        {fault === 'slow' && trip.arrived && (
          <p className="g-body cou-note">
            <b>It still arrived.</b> {f.verdict} The answer to the question here is <b>{f.blame}</b> —
            and notice the machine's clock never moved.
          </p>
        )}
        <p className="g-body g-dim cou-q"><Value of={cited(THE_QUESTION, FAULTS_SOURCE)} /></p>
      </div>

      {/* ── §7, because the translation is the payoff of the whole chapter ── */}
      <details className="cou-tr">
        <summary className="g-label">What the marketing words actually mean</summary>
        <dl className="cou-dl">
          {TRANSLATIONS.map((t) => (
            <div key={t.phrase}><dt>{t.phrase}</dt><dd>{t.means}</dd></div>
          ))}
        </dl>
        <p className="g-body g-dim"><Value of={cited('None of these are lies. They are just shorter than the truth.', TRANSLATIONS_SOURCE)} /></p>
      </details>

      <p className="g-body g-dim lab-why">
        Nothing here is measured. Every figure is the module's own —
        <Value of={cited('20 milliseconds · 250 milliseconds · 3 seconds', PLACES_SOURCE)} /> — depicted at a
        watchable pace, not at the real one: three seconds of village latency would be three seconds
        of nothing, and twenty milliseconds would be invisible. The bars and the timings are his
        numbers; the speed of the walk is not one of them. Individual packets are not drawn either,
        because neither this lab nor the module knows how many there are.
        <br />
        <Value of={cited('The five steps: translation, request, journey, work, reply.', STEPS_SOURCE)} />
      </p>
    </div>
  );
}
